import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import { API_BASE_URL, ACCESS_TOKEN } from '../../constants';
import { useAuth } from '../../auth/AuthHandler';

const WebSocketContext = createContext();
export const useWebSocketContext = () => useContext(WebSocketContext);

const getStoredConnectionStatus = () => {
  try { return sessionStorage.getItem('ws_connected') === 'true'; } catch { return false; }
};

const setStoredConnectionStatus = (status) => {
  try { sessionStorage.setItem('ws_connected', status ? 'true' : 'false'); } catch {}
};

const pushUniqueKey = (setRef, key, max = 200) => {
  if (!key) return false;
  if (setRef.current.has(key)) return false;

  setRef.current.add(key);

  if (setRef.current.size > max) {
    const first = setRef.current.values().next().value;
    setRef.current.delete(first);
  }

  return true;
};

export const WebSocketProvider = ({ children }) => {
  const { authenticated, userEmail } = useAuth();
  const location = useLocation();

  const [isConnected, setIsConnected] = useState(getStoredConnectionStatus());
  const [alertMessage, setAlertMessage] = useState(null);
  const [pendingAlert, setPendingAlert] = useState([]);
  const [mainRefresh, setMainRefresh] = useState(false);
  const [manageUserRefresh, setManageUserRefresh] = useState(null);
  const [ticketEvents, setTicketEvents] = useState([]);
  const [latestTicketEvents, setLatestTicketEvents] = useState([]);
  const [readEvents, setReadEvents] = useState([]);

  const clientRef = useRef(null);
  const socketRef = useRef(null);
  const subsRef = useRef({});
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const connectingRef = useRef(false);
  const lastTicketRef = useRef(null);

  const seenAlertKeysRef = useRef(new Set());
  const seenTicketKeysRef = useRef(new Set());
  const seenReadKeysRef = useRef(new Set());

  const [ticketId, setTicketId] = useState(null);

  useEffect(() => {
    const parts = location.pathname.split('/');
    setTicketId(parts.length > 2 && parts[1] === 'view-ticket' ? parts[2] : null);
  }, [location]);

  useEffect(() => {
    seenTicketKeysRef.current.clear();
    seenReadKeysRef.current.clear();
    setTicketEvents([]);
    setReadEvents([]);
  }, [ticketId]);

  const updateConnectionStatus = useCallback((status) => {
    setIsConnected(prev => {
      if (prev === status) return prev;
      setStoredConnectionStatus(status);
      return status;
    });
  }, []);

  const cleanupSubscriptions = useCallback(() => {
    Object.values(subsRef.current).forEach((sub) => {
      try { sub?.unsubscribe?.(); } catch {}
    });
    subsRef.current = {};
  }, []);

  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    try {
      cleanupSubscriptions();
      clientRef.current?.disconnect?.();
    } catch {}
    clientRef.current = null;
    socketRef.current = null;
    connectingRef.current = false;
    updateConnectionStatus(false);
  }, [cleanupSubscriptions, updateConnectionStatus]);

  const setupUserAndGlobalSubscriptions = useCallback((client) => {
    if (subsRef.current.alert) {
      subsRef.current.alert.unsubscribe();
      delete subsRef.current.alert;
    }
    if (subsRef.current.refresh) {
      subsRef.current.refresh.unsubscribe();
      delete subsRef.current.refresh;
    }

    if (userEmail) {
      subsRef.current.alert = client.subscribe(`/topic/alert/${userEmail}`, (msg) => {
        try {
          const data = JSON.parse(msg.body);
          const key = data?.msgId || data?.clientMessageId || JSON.stringify(data);
          if (!pushUniqueKey(seenAlertKeysRef, key)) return;

          if (data.type === 'REFRESH-LEAVE') {
            setManageUserRefresh(data);
          } else {
            setAlertMessage(data);
            if (data.type === 'PENDING') {
              setPendingAlert(prev => [...prev, data]);
            }
          }
        } catch (e) {
          console.error('Alert parse error:', e);
        }
      });
    }

    subsRef.current.refresh = client.subscribe('/topic/refresh', (msg) => {
      try {
        const data = JSON.parse(msg.body);
        if (data?.type === 'REFRESH-HOME') {
          setMainRefresh(true);
        }
      } catch (e) {
        console.error('Refresh parse error:', e);
      }
    });
  }, [userEmail]);

  const setupTicketSubscriptions = useCallback((client, currentTicketId) => {
    if (lastTicketRef.current === currentTicketId) return;

    if (subsRef.current.ticket) {
      subsRef.current.ticket.unsubscribe();
      delete subsRef.current.ticket;
    }
    if (subsRef.current.msgRead) {
      subsRef.current.msgRead.unsubscribe();
      delete subsRef.current.msgRead;
    }

    lastTicketRef.current = currentTicketId;

    seenTicketKeysRef.current.clear();
    seenReadKeysRef.current.clear();
    setTicketEvents([]);
    setReadEvents([]);

    if (!currentTicketId) return;

    subsRef.current.ticket = client.subscribe(`/topic/ticket/${currentTicketId}`, (msg) => {
      try {
        const data = JSON.parse(msg.body);
        const key = data?.msgId || data?.clientMessageId || JSON.stringify(data);
        if (!pushUniqueKey(seenTicketKeysRef, key)) return;
        setLatestTicketEvents(data);
        setTicketEvents(prev => [
          ...prev,
          { ...data, _eventKey: key, _receivedAt: Date.now() },
        ]);
      } catch (e) {
        console.error('Ticket message parse error:', e);
      }
    });

    subsRef.current.msgRead = client.subscribe(`/topic/msg-read/${currentTicketId}`, (msg) => {
      try {
        const data = JSON.parse(msg.body);
        const key = data?.msgId || data?.clientMessageId || JSON.stringify(data);
        if (!pushUniqueKey(seenReadKeysRef, key)) return;

        setReadEvents(prev => [
          ...prev,
          { ...data, _eventKey: key, _receivedAt: Date.now() },
        ]);
      } catch (e) {
        console.error('Msg-read parse error:', e);
      }
    });
  }, []);

  const handleReconnect = useCallback(() => {
    if (!authenticated || connectingRef.current) return;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const delay = Math.min(10000, 1000 * (2 ** Math.floor(Math.random() * 4)));
    reconnectTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, delay);
  }, [authenticated]);

  const connectWebSocket = useCallback(() => {
    if (!authenticated) return;
    if (connectingRef.current || clientRef.current?.connected) return;

    const token = localStorage.getItem(ACCESS_TOKEN);
    if (!API_BASE_URL || !token) return;

    try {
      cleanupSubscriptions();
      clientRef.current?.disconnect?.();
    } catch {}

    connectingRef.current = true;

    const socket = new SockJS(`${API_BASE_URL}/chat?token=${token}`);
    socketRef.current = socket;

    const client = Stomp.over(() => socket);
    clientRef.current = client;

    client.heartbeatIncoming = 4000;
    client.heartbeatOutgoing = 4000;
    client.reconnectDelay = 0;
    client.debug = process.env.NODE_ENV === 'development' ? console.log : () => {};

    client.onConnect = () => {
      connectingRef.current = false;
      updateConnectionStatus(true);

      setupUserAndGlobalSubscriptions(client);
      setupTicketSubscriptions(client, ticketId);

      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = setInterval(() => {
        if (!client.connected) {
          handleReconnect();
          return;
        }
        try {
          client.send('/app/heartbeat', {}, '{}');
        } catch {
          handleReconnect();
        }
      }, 5000);
    };

    client.onStompError = () => {
      connectingRef.current = false;
      updateConnectionStatus(false);
      handleReconnect();
    };

    client.onWebSocketClose = () => {
      connectingRef.current = false;
      updateConnectionStatus(false);
      handleReconnect();
    };

    try {
      client.activate();
    } catch {
      handleReconnect();
    }
  }, [
    authenticated,
    ticketId,
    cleanupSubscriptions,
    handleReconnect,
    setupTicketSubscriptions,
    setupUserAndGlobalSubscriptions,
    updateConnectionStatus,
  ]);

  useEffect(() => {
    if (!authenticated) {
      disconnectWebSocket();
      return;
    }
    connectWebSocket();
  }, [authenticated, userEmail, connectWebSocket, disconnectWebSocket]);

  useEffect(() => {
    const c = clientRef.current;
    if (!authenticated || !c?.connected) return;
    setupTicketSubscriptions(c, ticketId);
  }, [authenticated, ticketId, setupTicketSubscriptions]);

  useEffect(() => {
    const ensure = () => {
      const c = clientRef.current;
      if (authenticated && !c?.connected && !connectingRef.current) {
        connectWebSocket();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') ensure();
    };
    const onOnline = () => ensure();
    const onFocus = () => ensure();

    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authenticated, connectWebSocket]);

  const handleClosePendingNotification = useCallback((msgId) => {
    setPendingAlert(prev => prev.filter(n => n.msgId !== msgId));
  }, []);

  const value = useMemo(() => ({
    isConnected,
    stompClient: clientRef.current,
    ticketEvents,
    readEvents,
    alertMessage,
    mainRefresh,
    setMainRefresh,
    manageUserRefresh,
    latestTicketEvents
  }), [isConnected, ticketEvents, readEvents, alertMessage, mainRefresh, manageUserRefresh]);

  return (
    <WebSocketContext.Provider value={value}>
      <div>
        {children}
        {pendingAlert.length > 0 && (
          <div className="pending-notifications-list">
            <ul>
              {pendingAlert.map((n) => (
                <li key={n.msgId}>
                  <div className="pending-notification-item">
                    <p>{n.message}</p>
                    <button onClick={() => handleClosePendingNotification(n.msgId)} className="btn-close">
                      X
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </WebSocketContext.Provider>
  );
};